import { useAuthMutation } from './auth'
import { ApiStudentRequest, request } from '../utils/request'
import { useMyAccountAccessToken } from './useMyAccountAccessToken'
import { OKTA_MY_ACCOUNT_TOKEN_HEADER } from '../utils/okta/constants'
import {
  ValidateEmailRequest,
  ValidateEmailResponse,
} from '@backend/students-api/routes/profiles/{id}/validate_email/POST/types'
import { GetEmailChallengeResponse } from '@backend/students-api/routes/profiles/{id}/email_challenge/GET/types'
import {
  AddEmailChallengeRequest,
  AddEmailChallengeResponse,
} from '@backend/students-api/routes/profiles/{id}/add_email/email_challenge/POST/types'
import { VerifyEmailChallengeRequest } from '@backend/students-api/routes/profiles/{id}/add_email/email_challenge/PATCH/types'

type ValidateProfileEmailParams = ValidateEmailRequest['data']['attributes']
type AddEmailChallengeParams = AddEmailChallengeRequest['data']['attributes']
type VerifyEmailChallengeParams = VerifyEmailChallengeRequest['data']['attributes']

type ValidateEmailResponsePayload = ValidateEmailResponse
type EmailChallengeResponsePayload = GetEmailChallengeResponse
type AddEmailChallengeResponsePayload = AddEmailChallengeResponse

type ProfileEmailChangeProps = {
  id?: string
}

// Okta MyAccount endpoints only accept org-issuer tokens with okta.myAccount.* scopes,
// so we obtain that token in the UI and forward it to the Students BFF for UMS calls.
async function getMyAccountHeader(getMyAccountAccessToken: () => Promise<string>) {
  const token = await getMyAccountAccessToken()
  if (!token) {
    throw new Error('Unable to obtain Okta MyAccount access token.')
  }
  return { [OKTA_MY_ACCOUNT_TOKEN_HEADER]: token }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isValidateEmailResponse(value: unknown): value is ValidateEmailResponsePayload {
  if (!isRecord(value) || !isRecord(value.data)) return false
  const data = value.data
  if (data.type !== 'validate_email' || typeof data.id !== 'string') return false
  if (!isRecord(data.attributes)) return false
  return typeof data.attributes.valid === 'boolean'
}

function isEmailChallengeResponse(value: unknown): value is EmailChallengeResponsePayload {
  if (!isRecord(value) || !isRecord(value.data)) return false
  const data = value.data
  if (data.type !== 'email_challenge' || typeof data.id !== 'string') return false
  if (!isRecord(data.attributes)) return false
  return typeof data.attributes.emailId === 'string'
}

export function useValidateProfileEmail(props: ProfileEmailChangeProps) {
  const { getMyAccountAccessToken } = useMyAccountAccessToken()

  const { isPending, mutate } = useAuthMutation(async (params: ValidateProfileEmailParams) => {
    const headers = await getMyAccountHeader(getMyAccountAccessToken)
    return await request<ValidateEmailResponsePayload>(
      new ApiStudentRequest(`/profiles/${props.id}/validate_email`, {
        method: 'POST',
        headers,
        body: {
          data: {
            type: 'validate_email',
            attributes: {
              email: params.email,
            },
          },
        },
      }),
      {
        isExpectedResponse: isValidateEmailResponse,
      },
    )
  })

  return {
    isValidatingEmail: isPending,
    validateEmail: mutate,
  }
}

export function useGetProfileEmailChallenge(props: ProfileEmailChangeProps) {
  const { getMyAccountAccessToken } = useMyAccountAccessToken()

  const { isPending, mutate } = useAuthMutation(async () => {
    const headers = await getMyAccountHeader(getMyAccountAccessToken)
    return await request<EmailChallengeResponsePayload>(
      new ApiStudentRequest(`/profiles/${props.id}/email_challenge`, {
        method: 'GET',
        headers,
      }),
      {
        isExpectedResponse: isEmailChallengeResponse,
      },
    )
  })

  return {
    isCreatingEmailChallenge: isPending,
    createEmailChallenge: mutate,
  }
}

export function useAddProfileEmailChallenge(props: ProfileEmailChangeProps) {
  const { getMyAccountAccessToken } = useMyAccountAccessToken()

  const { isPending, mutate } = useAuthMutation(async (params: AddEmailChallengeParams) => {
    const headers = await getMyAccountHeader(getMyAccountAccessToken)
    return await request<AddEmailChallengeResponsePayload>(
      new ApiStudentRequest(`/profiles/${props.id}/add_email/email_challenge`, {
        method: 'POST',
        headers,
        body: {
          data: {
            type: 'email_challenge',
            attributes: {
              challengeId: params.challengeId,
              emailId: params.emailId,
              oneTimePass: params.oneTimePass,
              newEmail: params.newEmail,
            },
          },
        },
      }),
      {
        isExpectedResponse: isEmailChallengeResponse,
      },
    )
  })

  return {
    isSubmittingEmailChallenge: isPending,
    submitEmailChallenge: mutate,
  }
}

export function useVerifyProfileEmailChallenge(props: ProfileEmailChangeProps) {
  const { getMyAccountAccessToken } = useMyAccountAccessToken()

  const { isPending, mutate } = useAuthMutation(async (params: VerifyEmailChallengeParams) => {
    const headers = await getMyAccountHeader(getMyAccountAccessToken)
    return await request(
      new ApiStudentRequest(`/profiles/${props.id}/add_email/email_challenge`, {
        method: 'PATCH',
        headers,
        body: {
          data: {
            type: 'email_challenge',
            attributes: {
              challengeId: params.challengeId,
              emailId: params.emailId,
              oneTimePass: params.oneTimePass,
            },
          },
        },
      }),
      {
        isExpectedResponse: (res): res is unknown => true,
      },
    )
  })

  return {
    isVerifyingEmailChallenge: isPending,
    verifyEmailChallenge: mutate,
  }
}
